/**
 * MonitorDemoPage — `/demo`. Public, no auth, no shell.
 *
 * The investor / client / partner front door for the Monitor product: a
 * fully self-contained, no-signup walkthrough that *shows* the value in
 * ~30 seconds — a simulated phone privacy scan ("47 trackers found"), a
 * prominent "you saved $X" counter, and real-looking signed business
 * insights — then funnels to "Sign up to see YOUR real data".
 *
 * Everything on this page is SIMULATED, client-side, deterministic demo
 * data — there are zero API calls and nothing is persisted, so it loads
 * instantly and works for a cold visitor with no account. The figures are
 * clearly labelled as a demo (header pill + footer disclaimer) so it is
 * honest while still being convincing.
 *
 * Lives outside ProtectedRoute (App.tsx) — no sign-up wall. Mobile-first;
 * uses the GI brand tokens (spark/ink/canvas/surface) so it tracks light
 * and dark mode automatically. No external icon/animation libraries — RAF
 * count-ups + a scoped <style> block keep the bundle and the build risk
 * minimal.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { GiOrbitLogo } from '@/components/GiOrbitLogo';

// ─── Simulated data (clearly a demo) ─────────────────────────────────

const TRACKERS_FOUND = 47;

/** The 12 named trackers we surface (of the 47) — recognisable names make
 *  the "wow" land for a lay audience. */
const TRACKERS: ReadonlyArray<{ name: string; category: string; risk: 'high' | 'med' }> = [
  { name: 'Meta Pixel', category: 'Ad targeting', risk: 'high' },
  { name: 'Google Analytics', category: 'Analytics', risk: 'med' },
  { name: 'TikTok Pixel', category: 'Ad targeting', risk: 'high' },
  { name: 'Hotjar', category: 'Session replay', risk: 'high' },
  { name: 'Criteo', category: 'Retargeting', risk: 'high' },
  { name: 'FullStory', category: 'Session replay', risk: 'high' },
  { name: 'Segment', category: 'Data broker', risk: 'high' },
  { name: 'DoubleClick', category: 'Ad targeting', risk: 'med' },
  { name: 'Amplitude', category: 'Analytics', risk: 'med' },
  { name: 'Taboola', category: 'Content ads', risk: 'med' },
  { name: 'LinkedIn Insight', category: 'B2B tracking', risk: 'med' },
  { name: 'Microsoft Clarity', category: 'Session replay', risk: 'med' },
];

const SAVINGS_TARGET = 1284;

const SAVINGS_BREAKDOWN: ReadonlyArray<{ label: string; amount: number }> = [
  { label: 'Forgotten subscriptions caught', amount: 467 },
  { label: 'Price hikes blocked', amount: 312 },
  { label: 'Better-deal swaps', amount: 277 },
  { label: 'Overpayment refunds', amount: 228 },
];

type Severity = 'critical' | 'warning' | 'ok';

const INSIGHTS: ReadonlyArray<{
  severity: Severity;
  title: string;
  detail: string;
  impact?: string;
}> = [
  {
    severity: 'critical',
    title: 'A tracker on your checkout page is reading card fields',
    detail:
      'A third-party script (adtrack.js) attaches to form inputs on /checkout. We recommend removing or sandboxing it today.',
    impact: 'PCI risk',
  },
  {
    severity: 'warning',
    title: '3 subscriptions you forgot about',
    detail:
      'Renewals totalling $38.97/mo across your connected accounts — two have gone unused for 90+ days.',
    impact: 'save $467/yr',
  },
  {
    severity: 'ok',
    title: 'Price-hike shield is working',
    detail:
      'We caught a 22% increase on your cloud storage plan last month and held you at the old price.',
    impact: 'saved $54',
  },
];

const SEVERITY: Record<
  Severity,
  { label: string; chip: string; dot: string }
> = {
  critical: {
    label: 'Needs attention',
    chip: 'bg-danger/10 text-danger border-danger/30',
    dot: 'bg-danger',
  },
  warning: {
    label: 'Worth a look',
    chip: 'bg-warn/10 text-warn border-warn/30',
    dot: 'bg-warn',
  },
  ok: {
    label: 'Looking good',
    chip: 'bg-success/10 text-success border-success/30',
    dot: 'bg-success',
  },
};

// ─── Count-up hook (RAF, ease-out, replayable via runKey) ────────────

function useCountUp(target: number, durationMs: number, runKey: number): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf = 0;
    let startTs = 0;
    setValue(0);
    const tick = (ts: number) => {
      if (startTs === 0) startTs = ts;
      const p = Math.min(1, (ts - startTs) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, runKey]);
  return value;
}

const money = (n: number) => `$${n.toLocaleString('en-US')}`;

// ─── Phone scan ──────────────────────────────────────────────────────

function PhoneScan({ runKey, onRescan }: { runKey: number; onRescan: () => void }) {
  const [scanning, setScanning] = useState(true);
  const count = useCountUp(TRACKERS_FOUND, 2200, runKey);

  useEffect(() => {
    setScanning(true);
    const t = setTimeout(() => setScanning(false), 2300);
    return () => clearTimeout(t);
  }, [runKey]);

  return (
    <div className="flex flex-col items-center">
      {/* Phone frame */}
      <div className="relative h-[520px] w-[270px] rounded-[2.5rem] border-[10px] border-ink/85 bg-ink shadow-float">
        {/* notch */}
        <div className="absolute left-1/2 top-0 z-20 h-5 w-28 -translate-x-1/2 rounded-b-2xl bg-ink/85" />
        {/* screen */}
        <div className="relative h-full w-full overflow-hidden rounded-[1.8rem] bg-canvas">
          {/* sweep line while scanning */}
          {scanning && (
            <div
              className="pointer-events-none absolute inset-x-0 z-10 h-24"
              style={{
                background:
                  'linear-gradient(to bottom, transparent, rgb(var(--c-spark-hover) / 0.28), transparent)',
                animation: 'gi-demo-sweep 1.15s ease-in-out infinite',
              }}
            />
          )}

          <div className="flex h-full flex-col px-5 pb-5 pt-8">
            <div className="flex items-center gap-2 text-ink-muted">
              <GiOrbitLogo size="xs" decorative />
              <span className="text-xs font-semibold tracking-wide">Privacy scan</span>
              <span
                className={`ml-auto inline-flex items-center gap-1 text-[11px] font-medium ${
                  scanning ? 'text-spark' : 'text-success'
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${scanning ? 'bg-spark' : 'bg-success'}`}
                  style={scanning ? { animation: 'gi-demo-pulse 1s ease-in-out infinite' } : undefined}
                />
                {scanning ? 'Scanning…' : 'Done'}
              </span>
            </div>

            {/* big count */}
            <div className="mt-6 text-center">
              <div className="text-6xl font-bold tabular-nums text-ink">{count}</div>
              <div className="mt-1 text-sm font-medium text-ink-muted">
                {scanning ? 'trackers detected…' : 'trackers found watching you'}
              </div>
            </div>

            {/* tracker list */}
            <div className="mt-5 flex-1 space-y-1.5 overflow-hidden">
              {!scanning &&
                TRACKERS.map((t, i) => (
                  <div
                    key={t.name}
                    className="flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5"
                    style={{ animation: 'gi-demo-rise .4s ease both', animationDelay: `${i * 55}ms` }}
                  >
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                        t.risk === 'high' ? 'bg-danger' : 'bg-warn'
                      }`}
                    />
                    <span className="truncate text-xs font-medium text-ink">{t.name}</span>
                    <span className="ml-auto shrink-0 text-[10px] text-ink-subtle">{t.category}</span>
                  </div>
                ))}
            </div>

            {!scanning && (
              <div
                className="mt-3 rounded-lg bg-success/10 px-3 py-2 text-center text-xs font-semibold text-success"
                style={{ animation: 'gi-demo-rise .4s ease both', animationDelay: '700ms' }}
              >
                Blocked all {TRACKERS_FOUND} on this device — free, forever.
              </div>
            )}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onRescan}
        className="mt-5 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:border-border-strong hover:text-ink"
      >
        ↻ Run the scan again
      </button>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────

export function MonitorDemoPage() {
  const [runKey, setRunKey] = useState(0);
  const savings = useCountUp(SAVINGS_TARGET, 1700, runKey);

  return (
    <div className="min-h-screen bg-canvas text-ink">
      {/* scoped keyframes */}
      <style>{`
        @keyframes gi-demo-sweep { 0% { top: -20%; } 100% { top: 100%; } }
        @keyframes gi-demo-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes gi-demo-pulse { 0%, 100% { opacity: .35; } 50% { opacity: 1; } }
      `}</style>

      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-canvas/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <GiOrbitLogo size="sm" />
          <span className="font-semibold tracking-tight">Growing Intelligence</span>
          <span className="ml-2 rounded-full border border-spark/30 bg-spark-soft px-2.5 py-0.5 text-[11px] font-medium text-spark">
            Live demo · simulated data
          </span>
          <Link
            to="/signup"
            className="ml-auto rounded-lg bg-spark px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-spark-hover"
          >
            Sign up
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4">
        {/* Hero */}
        <section className="grid items-center gap-10 py-12 md:grid-cols-2 md:py-16">
          <div>
            <h1 className="text-4xl font-bold leading-tight tracking-tight md:text-5xl">
              See what&apos;s{' '}
              <span className="text-spark">watching you</span> — and what it&apos;s costing you.
            </h1>
            <p className="mt-4 max-w-md text-lg text-ink-muted">
              Growing Intelligence scans your apps and websites, blocks the trackers, catches the
              money you&apos;re leaking, and signs every finding so you can trust it. Try the live
              demo — no sign-up needed.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/signup"
                className="rounded-lg bg-spark px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-spark-hover"
              >
                See my real data → Sign up free
              </Link>
              <Link
                to="/app"
                className="rounded-lg border border-border bg-surface px-5 py-3 text-sm font-semibold text-ink transition-colors hover:border-border-strong"
              >
                Install the free app
              </Link>
            </div>
            <p className="mt-3 text-xs text-ink-subtle">
              Free tier: full scan, unlimited blocking, savings counter. No card required.
            </p>
          </div>

          <PhoneScan runKey={runKey} onRescan={() => setRunKey((k) => k + 1)} />
        </section>

        {/* Savings counter — prominent */}
        <section className="my-4 overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-spark-soft to-surface p-8 shadow-raised md:p-10">
          <div className="grid items-center gap-8 md:grid-cols-2">
            <div>
              <div className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
                Money we found for you
              </div>
              <div className="mt-2 text-6xl font-bold tabular-nums text-spark md:text-7xl">
                {money(savings)}
              </div>
              <div className="mt-1 text-base font-medium text-ink-muted">
                saved in the last 12 months
              </div>
              <button
                type="button"
                onClick={() => setRunKey((k) => k + 1)}
                className="mt-4 text-xs font-medium text-spark hover:underline"
              >
                ↻ Recount
              </button>
            </div>
            <div className="space-y-2.5">
              {SAVINGS_BREAKDOWN.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between rounded-lg border border-border bg-surface px-4 py-3"
                >
                  <span className="text-sm text-ink">{row.label}</span>
                  <span className="text-sm font-semibold tabular-nums text-success">
                    +{money(row.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Business insights */}
        <section className="py-12">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Insights, signed and ranked</h2>
              <p className="mt-1 text-ink-muted">
                Every finding is prioritised and cryptographically signed — so you know it&apos;s
                real, not a guess.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {INSIGHTS.map((ins) => {
              const s = SEVERITY[ins.severity];
              return (
                <article
                  key={ins.title}
                  className="flex flex-col rounded-xl border border-border bg-surface p-5 shadow-subtle"
                >
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${s.chip}`}
                    >
                      {s.label}
                    </span>
                    {ins.impact && (
                      <span className="ml-auto text-xs font-semibold text-ink-muted">
                        {ins.impact}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-3 text-base font-semibold leading-snug">{ins.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-ink-muted">{ins.detail}</p>
                  <div className="mt-4 flex items-center gap-1.5 text-[11px] font-medium text-ink-subtle">
                    <span className="text-success">✓</span>
                    verdict signed · GI Engine · Ed25519
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* Trust strip */}
        <section className="mb-12 rounded-xl border border-border bg-surface p-5 text-center">
          <p className="text-sm text-ink-muted">
            Tamper-proof by design — each verdict carries a signature anyone can verify.
          </p>
          <code className="mt-2 inline-block rounded-md bg-canvas px-3 py-1.5 font-mono text-xs text-ink-subtle">
            verdict 0x9af3c21…e84 · signed by GI Engine · Ed25519 ✓
          </code>
        </section>

        {/* Final CTA */}
        <section className="mb-16 rounded-2xl bg-spark p-8 text-center text-white md:p-12">
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
            This is simulated. Your real data is waiting.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-white/85">
            Sign up free and run a real scan on your own phone and websites in under 5 minutes. See
            your trackers, your savings, your signed insights.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/signup"
              className="rounded-lg bg-white px-6 py-3 text-sm font-semibold text-spark transition-transform hover:scale-[1.02]"
            >
              See YOUR real data → Sign up free
            </Link>
            <Link
              to="/monitor/start"
              className="rounded-lg border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              Connect a website
            </Link>
          </div>
        </section>
      </main>

      {/* Footer / disclaimer */}
      <footer className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-6 text-center text-xs text-ink-subtle">
          All numbers on this page are simulated for demonstration. Your real scan uses only the data
          you connect and stays private to you. · © Growing Intelligence
        </div>
      </footer>
    </div>
  );
}
